import { useCallback, useEffect, useRef, useState } from "react";

/* Load once per key, with the three states every panel needs.

   No data-fetching library: the app has a dozen read screens and none of them share a
   cache or refetch in the background. What they do need is for a slow response to a
   filter the user already changed not to overwrite the current one, which is what the
   abort and the sequence guard below are for. */
export function useApi(load, deps = [], { skip = false } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: !skip });
  const sequence = useRef(0);

  const run = useCallback(() => {
    if (skip) {
      setState({ data: null, error: null, loading: false });
      return () => {};
    }
    const ticket = ++sequence.current;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, loading: true, error: null }));
    load(controller.signal)
      .then((data) => {
        if (ticket === sequence.current) setState({ data, error: null, loading: false });
      })
      .catch((error) => {
        if (controller.signal.aborted || ticket !== sequence.current) return;
        setState({ data: null, error, loading: false });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, ...deps]);

  useEffect(run, [run]);
  return { ...state, reload: run };
}
